import LocalizationService from "services/localizationService";
import { action, computed, observable, reaction } from "mobx";
import { bound } from "@frui.ts/helpers";
import ScreenBase from "@frui.ts/screens/dist/structure/screenBase";
import { interfaces } from "inversify";
import ChargeOrder from "models/chargeOrder";
import ChargeOrderService from "services/chargeOrderService";
import AppRouter from "services/appRouter";
import ContactViewModel from "../steps/contactViewModel";
import { getValidationMessage } from "@frui.ts/validation";
import { attachAutomaticBlurValidator } from "utils/validationPatch";
import { NotificationValidationRules } from "models/notificationPreferences";

export default class NotificationPreferencesRecapViewModel extends ScreenBase {
  @observable isExpanded = false;
  private unsubscriptions: Array<() => void> = [];

  constructor(
    public chargeOrder: ChargeOrder,
    private chargeOrderService: ChargeOrderService,
    private localizationService: LocalizationService,
    private router: AppRouter
  ) {
    super();
    attachAutomaticBlurValidator(this.notificationPreferences, NotificationValidationRules);
  }

  protected onActivate() {
    this.unsubscriptions.push(
      reaction(() => this.notificationPreferences._notificationEnabled, this.onNotificationCheckboxChanged)
    );
    this.isExpanded = this.notificationPreferences._notificationEnabled;
    return super.onActivate();
  }

  protected onDeactivate(isClosing: boolean) {
    this.unsubscriptions.forEach(unsubscribe => unsubscribe());
    this.unsubscriptions = [];
    return super.onDeactivate(isClosing);
  }

  @computed
  get notificationPreferences() {
    return this.chargeOrder.notificationPreferences;
  }

  @computed
  get isNotificationEnabled() {
    return this.notificationPreferences._notificationEnabled;
  }

  @computed
  get notificationEmail() {
    return this.notificationPreferences._emailAddressNotification;
  }

  @computed
  get notificationPhone() {
    return this.notificationPreferences.phoneNumber;
  }

  @computed
  get emptyState() {
    return this.localizationService.translateGeneral("eshop.order.summary.empty");
  }

  @computed
  get emailValidationError() {
    return getValidationMessage(this.notificationPreferences, "_emailAddressNotification");
  }

  @computed
  get phoneValidationError() {
    return getValidationMessage(this.notificationPreferences, "phoneNumber");
  }

  @computed
  get hasValidationError() {
    return this.isNotificationEnabled && (!!this.emailValidationError || !!this.phoneValidationError);
  }

  @computed
  get notificationInfoText() {
    const orderItem = this.chargeOrder.charges[0];
    if (!orderItem) {
      return this.localizationService.translateGeneral("eshop.order.notification.infoTextGeneric");
    }

    const chargeTypeService = this.chargeOrderService["chargeTypeService"];
    const chargeType = chargeTypeService?.findChargeType(orderItem._chargeTypeId);

    if (!chargeType?.notifyDaysBefore) {
      return this.localizationService.translateGeneral("eshop.order.notification.infoTextGeneric");
    }

    const countRegex = /%count%/g;
    const daysRegex = /%days%/g;
    const localizedDaysString =
      chargeType.notifyDaysBefore === 2
        ? this.localizationService.translateGeneral("eshop.order.contact.2days")
        : this.localizationService.translateGeneral("eshop.order.contact.days");
    const daysWithCount = localizedDaysString.replace(countRegex, chargeType.notifyDaysBefore.toString());

    return this.localizationService.translateGeneral("eshop.order.notification.infoText").replace(daysRegex, daysWithCount);
  }

  @action.bound
  prefillNotificationEmailIfNeeded() {
    const enabled = this.notificationPreferences._notificationEnabled;
    const notificationEmail = this.notificationPreferences._emailAddressNotification?.trim();
    const confirmationEmail = this.notificationPreferences.emailAddress?.trim();
    const phoneNumber = this.notificationPreferences.phoneNumber?.trim();

    this.isExpanded = enabled;

    if (enabled && !notificationEmail && confirmationEmail && !phoneNumber) {
      this.notificationPreferences._emailAddressNotification = confirmationEmail;
      this.notificationPreferences.emailNotificationEnabled = true;
      this.chargeOrderService.persistChargeOrder();
    }
  }

  @action.bound
  onNotificationCheckboxChanged() {
    const enabled = this.notificationPreferences._notificationEnabled;
    this.isExpanded = enabled;

    if (enabled) {
      this.prefillNotificationEmailIfNeeded();
    } else {
      // Clear notification data when checkbox is unchecked
      this.notificationPreferences._emailAddressNotification = undefined;
      this.notificationPreferences.emailNotificationEnabled = false;
      this.notificationPreferences.phoneNumber = undefined;
      this.notificationPreferences.smsNotificationEnabled = false;
      this.chargeOrderService.persistChargeOrder();
    }
  }

  @bound
  navigateToContact() {
    this.router.navigate(ContactViewModel);
  }

  @bound
  translate(code: string) {
    return this.localizationService.translateGeneral(`eshop.order.summary.${code}`);
  }

  @bound
  translateNotification(code: string) {
    return this.localizationService.translateGeneral(`eshop.order.notification.${code}`);
  }

  static Factory({ container }: interfaces.Context) {
    return (chargeOrder: ChargeOrder) => {
      return new NotificationPreferencesRecapViewModel(
        chargeOrder,
        container.get(ChargeOrderService),
        container.get(LocalizationService),
        container.get(AppRouter)
      );
    };
  }
}
